'use client';

import React, { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { BenzingaEdgeProvider, EdgeCheckoutHost } from '@benzinga/edge';
import { CustomError } from '@benzinga/templates';

import RegisterEffects from '../root/RegisterEffects';
import AdditionalScripts from '../root/AdditionalScripts';
import RootTracker from '../root/RootTrackers';
import { PageStructuredData } from '../root/PageStructuredData';
import { resolvePageConfig } from '../../_libs/site-shell/resolvePageConfig';
import { RouteMetaProvider } from './RouteMetaContext';
import PageTracking from './PageTracking';
import type { PageConfig } from './pageConfig';

export type { PageConfig } from './pageConfig';

interface PageProvidersProps {
  children: React.ReactNode;
  pageConfig?: PageConfig;
}

function mergePageConfig(pathnameConfig: PageConfig, pageConfig: PageConfig): PageConfig {
  return {
    ...pathnameConfig,
    ...pageConfig,
    pageTargeting: pathnameConfig.pageTargeting ?? pageConfig.pageTargeting,
    disableRaptiveFooter: pathnameConfig.disableRaptiveFooter ?? pageConfig.disableRaptiveFooter,
    disableRaptiveReadyOnPageLoad:
      pathnameConfig.disableRaptiveReadyOnPageLoad ?? pageConfig.disableRaptiveReadyOnPageLoad,
  };
}

export default function PageProviders({ children, pageConfig = {} }: PageProvidersProps) {
  const pathname = usePathname();
  const pathnamePageConfig = useMemo(() => resolvePageConfig(pathname), [pathname]);
  const mergedPageConfig = useMemo(
    () => mergePageConfig(pathnamePageConfig, pageConfig),
    [pathnamePageConfig, pageConfig],
  );

  return (
    <RouteMetaProvider>
      {mergedPageConfig.metaProps ? <PageStructuredData metaProps={mergedPageConfig.metaProps} /> : null}
      <RegisterEffects />
      <RootTracker pageProps={mergedPageConfig} />
      <AdditionalScripts
        pageProps={{
          embeddedWidget: mergedPageConfig.embeddedWidget,
          disablePushPrompt: mergedPageConfig.disablePushPrompt ?? true,
        }}
      />
      <BenzingaEdgeProvider
        disableRaptiveFooter={mergedPageConfig.disableRaptiveFooter ?? false}
        disableRaptiveReadyOnPageLoad={mergedPageConfig.disableRaptiveReadyOnPageLoad}
        initialQueryParams={pageConfig.queryParams as Record<string, string> | undefined}
        initialTargeting={mergedPageConfig.pageTargeting}
      >
        <EdgeCheckoutHost />
        {mergedPageConfig.error ? (
          <CustomError statusCode={mergedPageConfig.error} />
        ) : (
          <>
            {children}
            <PageTracking pageConfig={mergedPageConfig} />
          </>
        )}
      </BenzingaEdgeProvider>
    </RouteMetaProvider>
  );
}
